import { memo as Memo, useMemo, useCallback, useState, useRef } from 'react';
import { Select } from 'antd';

//* HOCs
import { withLanguageContext } from 'context';

//* Components
import BackButton from '../BackButton';
import Text from 'components/common/Text';
import Icon from 'components/common/Icon';
import Image from 'components/common/Image';
import FormSelect from 'components/common/FormSelect';
import CategoriesHorizontalSlider from '../CategoriesHorizontalSlider';

//* Styles
import LocationNavbarStyle from './style';

const { Option } = Select;

const LocationNavbar = Memo(({ categories, regions, selectCategory, selectedCategory, selectRegion, selectedRegion, filteredCategories, selectInfo, clearFilter, translate, selectSubCategory, selectedSubCategory }) => {
	//! States
	const [activeCatIndex, setActiveCatIndex] = useState();

	//! Refs
	const selectRef = useRef();

	//! Categories HTML
	const categoriesHTML = useMemo(() => {
		return (
			//! slide go to active category
			<CategoriesHorizontalSlider
				activeIndex={activeCatIndex}
				className='ln-categories'>
				{categories.map((cat, k) => {
					cat.id === selectedCategory?.id && setActiveCatIndex(k);

					return (
						<div
							key={k}
							onClick={() => selectCategory(cat)}
							className={`ln-cat bg${cat.color}-hover ${cat.id === selectedCategory?.id ? `bg${cat.color} primaryColor3` : 'secondaryColor1-bg'}`}>
							<Text
								text={cat.name}
								className='font-ping-medium p pS'
							/>
						</div>
					);
				})}
			</CategoriesHorizontalSlider>
		);
	}, [categories, selectedCategory, selectCategory, activeCatIndex]);

	//! Sub categories HTML
	const subcategoriesHTML = useMemo(() => {
		const handleSubCategorySelect = (subcategory) => {
			const isCurrentSubcategoryExist = selectedSubCategory.find((item) => item.id === subcategory.id);

			if (!!isCurrentSubcategoryExist) {
				selectSubCategory(selectedSubCategory.filter((item) => item.id !== subcategory.id));
			} else {
				selectSubCategory([...selectedSubCategory, subcategory]);
			}
		};

		return (
			categories[activeCatIndex]?.children && (
				<>
					<div className='title-reset-wrap'>
						<Text
							className={'h6 font-ping-bold'}
							text={'chooseSubcategories'}
						/>
						<Text
							className={'pXS font-ping-bold secondaryColor2 underline reset-all'}
							text={'resetFilters'}
							onClick={() => selectSubCategory([])}
						/>
					</div>
					<CategoriesHorizontalSlider className='ln-subcategories'>
						{categories[activeCatIndex]?.children.map((cat, k) => {
							const isActive = !!selectedSubCategory.find((item) => item.id === cat.id);

							return (
								<div
									key={k}
									onClick={() => handleSubCategorySelect(cat)}
									className={`ln-cat subcategory border${cat.color}-hover ${isActive ? `border${cat.color} primaryColor3` : 'secondaryColor1-bg'}`}>
									<Text
										text={cat.name}
										className='font-ping-medium p pS'
									/>
								</div>
							);
						})}
					</CategoriesHorizontalSlider>
				</>
			)
		);
	}, [categories, activeCatIndex, selectedSubCategory, selectSubCategory]);

	//! Handle region change
	const handleRegionChange = useCallback((id) => {
		selectRegion(regions.find((region) => region.id == id));
	}, []);

	//! Regions HTML
	const regionsHTML = useMemo(() => {
		return (
			regions && (
				<>
					<Text
						text='chooseLocation'
						className='cat-sec-title h6 font-ping-bold '
					/>
					<div
						ref={selectRef}
						className='ln-regions-select-cont'>
						<FormSelect
							value={selectedRegion?.id || 'all'}
							onChange={handleRegionChange}
							className='ln-regions-select font-ping-medium p pS'
							suffixIcon={<Icon name='dropdown' />}
							getPopupContainer={(e) => selectRef.current}>
							<Option value={'all'}>{translate('allPlaces')}</Option>
							{regions.map((region, k) => (
								<Option
									key={k}
									value={region.id}>
									{region.name}
								</Option>
							))}
						</FormSelect>
					</div>
				</>
			)
		);
	}, [regions, selectRegion, selectedRegion]);

	//! Search results
	const searchResultsHtml = useMemo(() => {
		const filteredItems = filteredCategories
			.map((cat) =>
				cat.locations.map((loc, k) => {
					return (
						<div
							key={k}
							className={`ln-filtered-loc secondaryColor1-bg bg${cat.color}-hover`}
							onClick={() => selectInfo(loc)}>
							<div className='ln-filtered-loc-inner'>
								<div className='ln-image-cont'>
									<Image
										objectFit={'cover'}
										src={loc.image.url}
										alt={loc.image.alt}
									/>
								</div>

								<div className='ln-loc-info'>
									<Text
										text={loc.name}
										className='ln-loc-title font-ping-bold p primaryColor1'
									/>
									<Text
										text={loc.region_name}
										className='ln-loc-desc font-helvetica p pXS secondaryColor2'
									/>
								</div>
							</div>
						</div>
					);
				})
			)
			.flat(1);

		return (
			<>
				<div className='cat-sec-title ' />

				<div className='ln-filtered-items-cont'>
					{filteredItems.length ? (
						filteredItems
					) : (
						<Text
							text='noResultsFound'
							className='ln-loc-desc p pXS secondaryColor2'
						/>
					)}
				</div>
			</>
		);
	}, [filteredCategories, selectInfo]);

	return (
		<LocationNavbarStyle className={`${selectedCategory ? 'active' : ''}`}>
			{selectedCategory && (
				<>
					<BackButton onClick={clearFilter} />
					<div className='ln-info-container'>
						{categoriesHTML}

						{subcategoriesHTML}

						{regionsHTML}

						{searchResultsHtml}
					</div>
				</>
			)}
		</LocationNavbarStyle>
	);
});

export default withLanguageContext(LocationNavbar, ['translate']);
